import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { DrawerContentScrollView, DrawerItem, DrawerContentComponentProps } from '@react-navigation/drawer';
import { DrawerActions } from '@react-navigation/native';
import { useAuth } from '../hooks/useAuth';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { signOut } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return '#FF6B6B';
    case 'manager':
      return '#4ECDC4';
    case 'user':
      return '#45B7D1';
    default:
      return '#999';
  }
};

export function CustomDrawerContent(props: DrawerContentComponentProps) {
  const { appUser } = useAuth();
  const router = useRouter();
  const { t } = useLanguage();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const handleLogout = async () => {
    try {
      props.navigation.dispatch(DrawerActions.closeDrawer());
      await signOut();
      router.replace('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigation = (route: string) => {
    props.navigation.dispatch(DrawerActions.closeDrawer());
    router.push(route as any);
  };

  if (!appUser) {
    return null;
  }

  const role = appUser.role ?? 'guest';
  const canManage = role === 'admin' || role === 'manager';
  const canView = role !== 'guest';
  const isAdmin = role === 'admin';
  const displayName = [appUser.firstname, appUser.lastname].filter(Boolean).join(' ').trim();
  const initials = [appUser.firstname?.[0], appUser.lastname?.[0]].filter(Boolean).join('').toUpperCase() || appUser.email?.[0]?.toUpperCase() || '?';

  return (
    <View style={styles.root}>
      <DrawerContentScrollView {...props} contentContainerStyle={isDesktop ? styles.scrollContentDesktop : styles.scrollContent} style={styles.container}>
        {isDesktop ? (
          <>
            <View style={styles.desktopTitleBar}>
              <Text style={styles.desktopAppTitle}>GOPR Inventory</Text>
            </View>
            <View style={styles.desktopUserSection}>
              <View style={styles.desktopUserRow}>
                <View style={[styles.avatarCircle, { backgroundColor: getRoleColor(role) }]}>
                  <Text style={styles.avatarInitials}>{initials}</Text>
                </View>
                <View style={styles.desktopUserText}>
                  <Text style={styles.desktopUserName} numberOfLines={1}>{displayName || appUser.email}</Text>
                  <Text style={styles.desktopUserEmail} numberOfLines={1}>{appUser.email}</Text>
                </View>
                <View style={[styles.desktopRoleBadge, { backgroundColor: getRoleColor(role) }]}>
                  <Text style={styles.desktopRoleText}>{role.toUpperCase()}</Text>
                </View>
              </View>
            </View>
          </>
        ) : (
          <View style={styles.header}>
            <View style={styles.headerTop}>
              <Text style={styles.appTitle}>GOPR Inventory</Text>
            </View>
            <View style={styles.userInfo}>
              <Text style={styles.userName}>{displayName || appUser.email}</Text>
              <Text style={styles.userEmail}>{appUser.email}</Text>
              <View style={[styles.roleBadge, { backgroundColor: getRoleColor(role) }]}>
                <Text style={styles.roleText}>{role.toUpperCase()}</Text>
              </View>
            </View>
          </View>
        )}

        <View style={[styles.navigationSection, isDesktop && styles.navigationSectionDesktop]}>
          <DrawerItem
            label={t('tabs.home')}
            onPress={() => handleNavigation('/(app)/home')}
            style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
            labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
            icon={({ color, size }) => (
              <Ionicons name="home-outline" color={color} size={isDesktop ? 20 : size} />
            )}
          />
          <DrawerItem
            label={t('tabs.usage')}
            onPress={() => handleNavigation('/(app)/usage')}
            style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
            labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
            icon={({ color, size }) => (
              <Ionicons name="clipboard-outline" color={color} size={isDesktop ? 20 : size} />
            )}
          />
          {canView && (
            <>
              <DrawerItem
                label={t('operations.title')}
                onPress={() => handleNavigation('/(app)/tasks')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="checkbox-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
              <DrawerItem
                label={t('tabs.documents')}
                onPress={() => handleNavigation('/(app)/documents')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="document-text-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
              <DrawerItem
                label={t('tabs.stores')}
                onPress={() => handleNavigation('/(app)/stores')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="business-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
            </>
          )}
          {canManage && (
            <>
              <DrawerItem
                label={t('tabs.inventory')}
                onPress={() => handleNavigation('/(app)/categories')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="cube-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
              <DrawerItem
                label={t('tabs.reports')}
                onPress={() => handleNavigation('/(app)/reports')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="bar-chart-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
            </>
          )}
          {isAdmin && (
            <>
              <DrawerItem
                label={t('tabs.admin')}
                onPress={() => handleNavigation('/(app)/admin')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="shield-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
              <DrawerItem
                label={t('tabs.auditLogs')}
                onPress={() => handleNavigation('/(app)/audit-logs')}
                style={isDesktop ? styles.drawerItemDesktop : styles.drawerItem}
                labelStyle={isDesktop ? styles.drawerLabelDesktop : styles.drawerLabel}
                icon={({ color, size }) => (
                  <Ionicons name="list-outline" color={color} size={isDesktop ? 20 : size} />
                )}
              />
            </>
          )}
        </View>
      </DrawerContentScrollView>

      {isDesktop ? (
        <View style={styles.desktopBottomSection}>
          <TouchableOpacity style={styles.desktopBottomItem} onPress={() => handleNavigation('/(app)/profile')}>
            <Ionicons name="person-outline" size={18} color="#555" />
            <Text style={styles.desktopBottomItemText}>{t('tabs.profile')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.desktopBottomItem} onPress={() => handleNavigation('/(app)/contact')}>
            <Ionicons name="information-circle-outline" size={18} color="#555" />
            <Text style={styles.desktopBottomItemText}>{t('drawer.contactLabel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.desktopLogoutButton} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={18} color="#d32f2f" />
            <Text style={styles.desktopLogoutText}>{t('drawer.logout')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.bottomSection}>
          <TouchableOpacity
            style={styles.profileButton}
            onPress={() => handleNavigation('/(app)/profile')}
          >
            <Ionicons name="person-outline" size={20} color="#0066CC" />
            <Text style={styles.profileButtonText}>{t('tabs.profile')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.contactButton}
            onPress={() => handleNavigation('/(app)/contact')}
          >
            <Ionicons name="information-circle-outline" size={20} color="#0066CC" />
            <Text style={styles.contactButtonText}>{t('drawer.contactLabel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={20} color="#fff" />
            <Text style={styles.logoutText}>{t('drawer.logout')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollContent: {
    flexGrow: 1,
  },
  scrollContentDesktop: {
    flexGrow: 1,
    paddingTop: 0,
    paddingBottom: 0,
    paddingStart: 0,
    paddingEnd: 0,
  },

  // ── Mobile header ──────────────────────────────────────────────
  header: {
    backgroundColor: '#0066CC',
    padding: 20,
    paddingTop: 20,
    marginTop: -5,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  appTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
  },
  userInfo: {
    marginTop: 10,
  },
  userName: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: '#e3f2fd',
    marginBottom: 8,
  },
  roleBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  roleText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },

  // ── Desktop header ─────────────────────────────────────────────
  desktopTitleBar: {
    height: 56,
    backgroundColor: '#0066CC',
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  desktopUserSection: {
    backgroundColor: '#004fa3',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  desktopAppTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#fff',
    letterSpacing: 0.3,
  },
  desktopUserRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  avatarCircle: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarInitials: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  desktopUserText: {
    flex: 1,
    minWidth: 0,
  },
  desktopUserName: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  desktopUserEmail: {
    fontSize: 12,
    color: '#cce4ff',
    marginTop: 1,
  },
  desktopRoleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    flexShrink: 0,
  },
  desktopRoleText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },

  // ── Navigation ─────────────────────────────────────────────────
  navigationSection: {
    flex: 1,
    paddingTop: 20,
  },
  navigationSectionDesktop: {
    paddingTop: 8,
  },
  drawerItem: {
    marginHorizontal: 10,
    borderRadius: 8,
  },
  drawerLabel: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  drawerItemDesktop: {
    marginHorizontal: 8,
    marginVertical: 1,
    borderRadius: 6,
  },
  drawerLabelDesktop: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },

  // ── Mobile bottom section ──────────────────────────────────────
  bottomSection: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    padding: 16,
    paddingBottom: 30,
    backgroundColor: '#f8f9fa',
  },
  contactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0066CC',
    justifyContent: 'center',
    marginBottom: 8,
  },
  contactButtonText: {
    color: '#0066CC',
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },
  profileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0066CC',
    justifyContent: 'center',
    marginBottom: 8,
  },
  profileButtonText: {
    color: '#0066CC',
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#d32f2f',
    padding: 12,
    borderRadius: 8,
    justifyContent: 'center',
    marginTop: 4,
  },
  logoutText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },

  // ── Desktop bottom section ─────────────────────────────────────
  desktopBottomSection: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#f8f9fa',
    gap: 2,
  },
  desktopBottomItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderRadius: 6,
    gap: 10,
  },
  desktopBottomItemText: {
    fontSize: 14,
    color: '#555',
    fontWeight: '500',
  },
  desktopLogoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginTop: 2,
    gap: 10,
  },
  desktopLogoutText: {
    fontSize: 14,
    color: '#d32f2f',
    fontWeight: '600',
  },
});
